/**
 * Lap Mappers
 *
 * Bi-directional conversion between Lap domain model and LapDto
 */

import { Lap } from '../../domain/lap/Lap'
import { LapBounds } from '../../domain/lap/LapBounds'
import { LapMetadata } from '../../domain/lap/LapMetadata'
import { LapDto, TelemetryDto } from '../apiTypes'
import { computeBreakingPoints } from '../../domain/lap/lapAnalysis'
import { processRawTelemetry } from '../../processors/rawTelemetryProcessor'
import { createSpatialBuckets } from '../../processors/spatial/spatialProcessing'
import { telemetryDtoToRaw } from './telemetryMapper'

/**
 * Convert Lap domain model to LapDto for API calls
 *
 * This is used when saving lap data to the backend.
 *
 * Telemetry is optional. Pass it explicitly when the DTO must carry its own
 * telemetry (e.g. sharing a single lap). Omit it when the telemetry is
 * already being sent elsewhere in the request (e.g. saving a whole track
 * session, where per-lap telemetry would otherwise duplicate the
 * session-level telemetry payload for every lap).
 *
 * @param lap - The Lap domain model
 * @param telemetry - Optional telemetry DTO (convert from RawTelemetryData using rawTelemetryToDto)
 * @returns LapDto ready for API submission; `telemetry` is omitted when not provided
 */
export function lapToDto(lap: Lap, telemetry?: TelemetryDto): LapDto {
  return {
    id: lap.id,
    lapNumber: lap.lapNumber,
    startTime: lap.startTime,
    finishTime: lap.finishTime,
    lapTimeMs: lap.lapTime,
    trackSessionId: lap.sessionId,
    trackId: lap.trackId,
    weather: lap.weather,
    driverName: lap.driverName,
    ...(telemetry !== undefined ? { telemetry } : {}),
  }
}

/**
 * Convert a LapDto (with telemetry) to a Lap domain model
 *
 * This processes raw telemetry from the DTO through the telemetry processor
 * and creates a fully-initialized Lap instance with breaking points and analysis.
 *
 * Used when loading individual shared laps from the API for comparison.
 *
 * @param lapDto - The LapDto from the API (must include telemetry)
 * @returns Lap domain model ready for analysis, or null if telemetry is missing
 */
export function lapDtoToLap(lapDto: LapDto): Lap | null {
  if (!lapDto.telemetry) {
    return null
  }

  const rawTelemetry = telemetryDtoToRaw(
    lapDto.telemetry,
    new Date(lapDto.startTime)
  )

  const processedTelemetry = processRawTelemetry(
    rawTelemetry,
    rawTelemetry.recordingStartTime ?? new Date(lapDto.startTime),
    {
      calculateLeanAngle: true,
      calculateAcceleration: true,
      calculateGForce: true,
    }
  )

  // Normalize distances to start from 0 (lap-relative).
  // processRawTelemetry preserves cumulative session distances from distanceTravelled,
  // but individual laps need 0-based distances (same as TrackSession.extractLapProcessedTelemetry).
  const startDistance = processedTelemetry.gpsPoints[0]?.distanceMeters ?? 0
  if (startDistance !== 0) {
    processedTelemetry.gpsPoints = processedTelemetry.gpsPoints.map((p) => ({
      ...p,
      distanceMeters: p.distanceMeters - startDistance,
    }))
    processedTelemetry.leanAngle = processedTelemetry.leanAngle.map((p) => ({
      ...p,
      distanceMeters: p.distanceMeters - startDistance,
    }))
    processedTelemetry.acceleration = processedTelemetry.acceleration.map(
      (p) => ({
        ...p,
        distanceMeters: p.distanceMeters - startDistance,
      })
    )
    processedTelemetry.gForce = processedTelemetry.gForce.map((p) => ({
      ...p,
      distanceMeters: p.distanceMeters - startDistance,
    }))
  }

  const metadata: LapMetadata = {
    lapId: lapDto.id,
    lapNumber: lapDto.lapNumber,
    driverName: lapDto.driverName ?? 'Unknown',
    trackSessionId: lapDto.trackSessionId ?? '',
    trackId: lapDto.trackId,
  }

  const bounds: LapBounds = {
    startTime: new Date(lapDto.startTime),
    finishTime: new Date(lapDto.finishTime),
    startDistanceMeters: 0,
    finishDistanceMeters:
      processedTelemetry.gpsPoints[processedTelemetry.gpsPoints.length - 1]
        ?.distanceMeters ?? 0,
  }

  const spatialBuckets = createSpatialBuckets(processedTelemetry)

  const lap = new Lap(metadata, bounds, processedTelemetry, rawTelemetry, {
    device: lapDto.telemetry.device,
    weather: lapDto.weather,
    gyro: rawTelemetry.gyroscope,
    accl: rawTelemetry.accelerometer,
    spatialBuckets,
  })

  lap.isSelected = true
  lap.isShared = true

  const result = computeBreakingPoints(lap)
  lap.breakingPoints = result.breakingPoints
  lap.slowestPoints = result.slowestPoints
  lap.topSpeed = result.topSpeed

  return lap
}
